/**
 * The HG media library as a pick list — ONE component, used by the newsletter image panel
 * (`use=newsletter`) and by Social Studio's "Insert from media library" (`use=social`).
 *
 * It was written inline in EmailImagePanel first. The films queue-up command (9 Sep 2026)
 * asked for the same picker in Social Studio, and a second copy would be a second answer to
 * "how does a library item render" — the two drift the first time one is fixed. So both screens
 * render this, and each says only what differs: what a pick DOES, what the pick button is
 * called, and why an item cannot be picked here.
 *
 * WHAT IT RENDERS, BY KIND — never by resource_type. Cloudinary files audio under
 * resource_type "video", so anything keying off that would ask for a still frame of a sound
 * file and show a broken thumbnail.
 *   film  -> its poster, title, duration (and orientation — Instagram needs to see "vertical")
 *   audio -> a play control, title, duration. No poster: there is no frame to show.
 *   photo -> the image itself.
 *
 * THE LIST NEVER DECIDES WHAT IS APPROVED. It renders what the feed returned, and the feed
 * serves only `status=approved`. A `blockReason` is shown in words next to a disabled button —
 * never a silently missing item, because an absent film reads as "there is no film".
 */
import { Film, Link2 } from 'lucide-react';

// Dark amber on the Hub's light ground. Tailwind's amber-300 is a dark-theme value and renders
// pale-on-pale here; this pair was picked for contrast on the off-white surface.
const WARN = 'text-[#8a5a00]';

export function durationText(d) {
  if (d === null || d === undefined || Number.isNaN(Number(d))) return '';
  const s = Math.round(Number(d));
  return s >= 60 ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : `${s}s`;
}

export function MediaLibraryList({
  items = [],
  testIdPrefix = 'media',
  onPick,
  pickLabel,
  blockReason,
  note,
  busy = false,
  showDownload = false,
  title = 'HG media library',
}) {
  if (!items.length) return null;
  return (
    <div className="border border-hg-border rounded-sm p-2 bg-hg-bg/40 space-y-2" data-testid={`${testIdPrefix}-media-library`}>
      <div className="flex items-center gap-1.5 text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">
        <Film size={11} /> {title}
      </div>
      {items.map((m) => {
        const reason = blockReason ? blockReason(m) : null;
        const label = pickLabel ? pickLabel(m) : null;
        const pickable = Boolean(onPick) && !reason;
        const thumbPick = pickable && m.kind !== 'audio';
        let thumb;
        if (m.kind === 'audio') {
          thumb = (
            <audio data-testid={`${testIdPrefix}-media-audio`} controls preload="none" src={m.url}
                   className="w-40 shrink-0 h-8" />
          );
        } else {
          const src = m.poster_url || m.url;
          const img = (
            <img src={src} alt={m.title}
                 className={`w-full h-full ${m.poster_url ? 'object-cover' : 'object-contain bg-hg-bg'}`}
                 loading="lazy" />
          );
          thumb = (
            <button
              data-testid={`${testIdPrefix}-media-${m.poster_url ? 'poster' : 'image'}`}
              onClick={() => thumbPick && onPick(m)}
              disabled={busy || !thumbPick}
              title={m.title}
              className="w-16 h-10 shrink-0 rounded-sm overflow-hidden border border-hg-border hover:border-hg-green transition-colors disabled:opacity-60"
            >
              {img}
            </button>
          );
        }
        return (
          <div key={m.id} className="flex items-center gap-2" data-media-id={m.id}>
            {thumb}
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-sans text-hg-text truncate">{m.title}</div>
              <div className="text-[10px] font-sans text-hg-text-dim">
                {m.kind}
                {m.duration ? ` · ${durationText(m.duration)}` : ''}
                {m.kind === 'film' && m.orientation ? ` · ${m.orientation}` : ''}
                {note ? note(m) : ''}
              </div>
              {reason && (
                <div data-testid={`${testIdPrefix}-media-block`} className={`text-[10px] font-sans ${WARN}`}>{reason}</div>
              )}
            </div>
            {label && onPick && (
              <button
                data-testid={`${testIdPrefix}-media-pick`}
                onClick={() => pickable && onPick(m)}
                disabled={busy || !pickable}
                className="shrink-0 text-[10px] font-sans uppercase tracking-wider border border-hg-border text-hg-text rounded-sm px-2 py-1 disabled:opacity-40"
              >
                {label}
              </button>
            )}
            {showDownload && (
              <a
                data-testid={`${testIdPrefix}-media-download`}
                href={m.download_url}
                className="shrink-0 inline-flex items-center gap-1 text-[10px] font-sans text-hg-text-dim hover:text-hg-green border border-hg-border rounded-sm px-1.5 py-1"
                title={m.download_url}
              >
                <Link2 size={10} /> Download
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default MediaLibraryList;
